import {
  MapPin,
  ShieldEllipsis,
  User,
  Users,
  ShoppingCart,
  Check,
  Star,
  Gift,
  Palette,
  Megaphone,
  TrendingUp,
  BarChart3,
  QrCode,
  Calendar,
  Search,
  Layers,
  ShoppingBag,
  MessageCircle,
  ArrowLeft,
} from "lucide-react";
import React, { useState } from "react";
import { getAddonByTypeId, getDurationOptions } from "../api/addons.js";
import { useNavigate } from "react-router-dom";
import { addToCart } from "../utils/cart.js";
import PriceCalculator from "./PriceCalculator.jsx";
import PriceCalculatorUI from "./PriceCalculatorUI.jsx";

// Icon mapping for different addon types
const ICON_MAP = {
  1: { icon: User, color: "text-orange-500" }, // Additional User
  2: { icon: ShieldEllipsis, color: "text-blue-500" }, // Permission
  3: { icon: MapPin, color: "text-green-500" }, // Multi-Locations
  4: { icon: Search, color: "text-purple-500" }, // Search Optimization
  5: { icon: Star, color: "text-yellow-500" }, // Stamps
  6: { icon: TrendingUp, color: "text-indigo-500" }, // Points
  7: { icon: Palette, color: "text-pink-500" }, // Customized Design Stamps
  8: { icon: Gift, color: "text-red-500" }, // Gift Card
  9: { icon: Palette, color: "text-pink-500" }, // Customized Design Gift
  10: { icon: Megaphone, color: "text-orange-500" }, // Social Media A
  11: { icon: MessageCircle, color: "text-blue-500" }, // Social Media B
  12: { icon: Layers, color: "text-teal-500" }, // Social Media C
  13: { icon: ShoppingBag, color: "text-green-500" }, // Order
  14: { icon: Calendar, color: "text-purple-500" }, // Reservations
  15: { icon: Users, color: "text-blue-500" }, // Customer Management
  16: { icon: BarChart3, color: "text-indigo-500" }, // Analytical Dashboard
  17: { icon: Megaphone, color: "text-red-500" }, // Interactive Advertisement
  18: { icon: QrCode, color: "text-gray-500" }, // Customized Menu QR
};

const AddonPage = ({ addonTypeId }) => {
  const navigate = useNavigate();

  // Get addon data from config
  const addonData = getAddonByTypeId(addonTypeId);
  const durationOptions = getDurationOptions(addonData);
  const iconData = ICON_MAP[addonTypeId] || {
    icon: Star,
    color: "text-gray-500",
  };
  const IconComponent = iconData.icon;

  // Get default duration (first option)
  const defaultDuration =
    durationOptions.length > 0 ? durationOptions[0] : null;

  const [quantity, setQuantity] = useState(1);
  const [selectedDuration, setSelectedDuration] = useState(defaultDuration);
  const [message, setMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  // Calculate total amount based on selected duration
  const calculateTotal = () => {
    if (!selectedDuration) return 0;
    return selectedDuration.price * quantity;
  };

  const handleAddToPlan = () => {
    // Save addon selection to cart (localStorage)
    const cartItem = {
      addonTypeId: addonTypeId,
      addonName: addonData?.name || "Addon",
      quantity: quantity,
      duration: selectedDuration?.backendValue || 3,
      durationLabel: selectedDuration?.label || "selected duration",
      price: calculateTotal(),
      isOnetime: addonData?.is_onetime || false,
    };

    addToCart(cartItem);

    setIsSuccess(true);
    setMessage(
      `Added ${quantity} ${addonData?.name || "addon"}(s) for ${selectedDuration?.label || "selected duration"} to your cart!`,
    );

    // Navigate to price page after 1 second - scroll to Final Quote & Features
    setTimeout(() => {
      navigate("/price#code-and-feature");
    }, 1000);
  };

  if (!addonData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading addon data...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="container">
        <div id="hero-section" className="py-20 px-5 bg-[#D9D9D9]">
          <div className="mx-auto w-1/2 space-y-3">
            <div className="flex items-center gap-3">
              <IconComponent className={`h-10 w-10 ${iconData.color}`} />
              <h1 className="text-3xl font-bold">{addonData.name}</h1>
            </div>
            <h3 className="text-xl font-semibold">{addonData.description}</h3>
            <p className="space-x-1">
              {addonData.is_onetime ? (
                <b className="text-secondary-color">One-time purchase</b>
              ) : (
                <b className="text-secondary-color">Subscription</b>
              )}
              <b className="text-primary-color">{addonData.unit}</b>
            </p>

            {/* Duration Selection - Dropdown with months prefix */}
            {!addonData.is_onetime && durationOptions.length > 0 && (
              <div className="py-3">
                <span className="text-lg font-medium block mb-2">
                  Select Duration:
                </span>
                <select
                  value={selectedDuration?.uiValue || ""}
                  onChange={(e) => {
                    const selected = durationOptions.find(
                      (opt) => opt.uiValue === parseInt(e.target.value),
                    );
                    setSelectedDuration(selected);
                  }}
                  className="border border-gray-300 cursor-pointer p-3 rounded w-full outline-none bg-white"
                >
                  {durationOptions.map((option) => (
                    <option key={option.uiValue} value={option.uiValue}>
                      {option.label} - {option.price.toLocaleString()} MMK
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* One-time price display */}
            {addonData.is_onetime && addonData.prices.onetime_price > 0 && (
              <div className="py-3">
                <span className="text-lg font-medium">One-time Price: </span>
                <span className="text-2xl font-bold text-primary-color">
                  {addonData.prices.onetime_price.toLocaleString()} MMK
                </span>
              </div>
            )}

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 py-3">
              <span className="text-lg font-medium">Quantity:</span>
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3 py-2 hover:bg-gray-100 rounded-l-lg transition-colors"
                  disabled={quantity <= 1}
                >
                  <span className="font-bold">-</span>
                </button>
                <span className="px-4 py-2 font-semibold min-w-[50px] text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-3 py-2 hover:bg-gray-100 rounded-r-lg transition-colors"
                >
                  <span className="font-bold">+</span>
                </button>
              </div>
              <span className="text-lg font-bold text-primary-color">
                Total: {calculateTotal().toLocaleString()} MMK
              </span>
            </div>

            {/* Success Message */}
            {isSuccess && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <Check className="w-5 h-5" />
                <span className="font-medium">{message}</span>
              </div>
            )}

            <div className="flex flex-row gap-3 items-center">
              <button
                onClick={handleAddToPlan}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all bg-blue-600 hover:bg-blue-700 text-white"
              >
                <ShoppingCart className="w-5 h-5" />
                Add to my plan
              </button>

              {/* Back Button */}
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 bg-white shadow-md rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            </div>

            {/* Back Button and Price Calculator (beside Add to my plan) */}
            <PriceCalculatorUI />
          </div>
        </div>

        <div
          id="content"
          className="text-white text-center px-10 py-8"
          style={{ background: "linear-gradient(to top, #FFFAFA, #273469)" }}
        >
          <div className="w-1/2 flex flex-col mx-auto space-y-3">
            <h3 className="text-xl font-bold mb-3">Key Features</h3>
            <p>{addonData.description}</p>
          </div>
        </div>

        {/* Features Grid */}
        <div id="body">
          <div className="grid grid-cols-4 gap-5 m-5 pt-5">
            {addonData.features && addonData.features.length > 0 ? (
              addonData.features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-100 rounded-xl space-y-3 text-center px-3 py-3"
                >
                  <div className="flex items-center justify-center text-blue-500">
                    <Check className="h-10 w-10" />
                  </div>
                  <h3 className="text-md font-bold">{feature}</h3>
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center text-gray-500 py-5">
                No additional features listed.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Price Calculator Popup */}
      {showCalculator && (
        <div className="fixed inset-0 bg-gray-200 bg-opacity-30 backdrop-blur-sm z-50 flex items-center justify-center">
          <div
            id="price-calculator-popup"
            className="fixed bottom-1/2 right-1/2 transform translate-x-1/2 translate-y-1/2 z-50 bg-gray-200 bg-opacity-80 backdrop-blur-md p-6 rounded-lg shadow-xl md:w-5/6 max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <PriceCalculator setShowCalculator={setShowCalculator} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddonPage;
